import { ref, computed } from 'vue'
import useApi from './useApi'

export default function useContractStats(initialStatuses = []) {
  const { getJSON } = useApi()

  const contractStats = ref(initializeContractStats(initialStatuses))
  const loading = ref(false)
  const error = ref(null)

  function initializeContractStats(statuses = []) {
    const stats = {}
    for (const status of statuses) {
      stats[status] = { count: 0, amount: 0 }
    }
    return stats
  }

  function mapAggregatedContractStats(data) {
    const mappedStats = { ...contractStats.value }

    for (let status of data) {
      if (status.fullAmount == null) {
        status.fullAmount = 0
      }

      let mappedStatus = null
      switch (status.status) {
        case 'FINALIZE':
        case 'CREATED':
          mappedStatus = 'FINALIZE'
          break
        case 'STUDY':
        case 'STUDIED':
        case 'VALIDATED':
        case 'PENDING':
          mappedStatus = 'SIGNING'
          break
        case 'CLIENT_SIGNED':
        case 'COMPANY_SIGNED':
        case 'CHECKED':
        case 'WAITING':
        case 'ORDERED':
          mappedStatus = 'DELIVERING'
          break
        case 'DELIVERED':
        case 'COMPLETE':
          mappedStatus = 'PAYING'
          break
        case 'PAID':
          mappedStatus = 'PAID'
          break
        case 'FINISH':
          mappedStatus = 'FINISH'
          break
        case 'BUYOUT':
          mappedStatus = 'BUYOUT'
          break
      }

      if (mappedStatus && mappedStats[mappedStatus]) {
        mappedStats[mappedStatus] = {
          count: parseInt((mappedStats[mappedStatus]?.count ?? 0) + status.nb),
          amount: (mappedStats[mappedStatus]?.amount ?? 0) + parseFloat(status.fullAmount),
        }
      }
    }

    return mappedStats
  }

  async function fetchContractStats(searchParams = {}) {
    loading.value = true
    error.value = null
    try {
      const query = new URLSearchParams()
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) query.append(key, value)
      })

      const stats = await getJSON(`contracts/stat${query.toString() ? '?' + query.toString() : ''}`)
      contractStats.value = mapAggregatedContractStats(stats)
    } catch (err) {
      error.value = err
    } finally {
      loading.value = false
    }
  }

  function getContractLink(status, isWallet = false) {
    if (isWallet) {
      return getContractLinkWallet(status)
    }

    const statusMapping = {
      FINALIZE: 'contrats/index.php?s=2',
      SIGNING: 'contrats/index.php?s=2',
      DELIVERING: 'contrats/index.php?s=3',
      PAYING: 'contrats/index.php?s=4',
      PAID: 'portefeuille/index.php?s=1',
      FINISH: 'portefeuille/index.php?s=2',
      BUYOUT: 'buyout/index.php?s=3',
    }
    return statusMapping[status] || 'contrats/index.php?s=2'
  }

  /**
   * Get contract link URL for wallet (platformClient) context
   * @param {String} status - Aggregated status category
   * @returns {String} Link URL for the contract status
   */
  function getContractLinkWallet(status) {
    const statusMapping = {
      FINALIZE: '/contrats/client.php?s=2',
      SIGNING: '/contrats/client.php?s=2',
      DELIVERING: '/contrats/client.php?s=3',
      PAYING: '/contrats/client.php?s=4',
      PAID: '/contrats/client.php?s=1',
      FINISH: '/portefeuille/client.php?s=1',
      CLOSED: '/portefeuille/client.php?s=2',
    }
    return statusMapping[status] || '/portefeuille/client.php?s=0'
  }

  return {
    contractStats,
    loading,
    error,
    fetchContractStats,
    getContractLink,
    getContractLinkWallet,
    initializeContractStats,
    mapAggregatedContractStats,
  }
}
